/**
 * Predefined filter configurations for common use cases
 */

// Common filter configurations
export const COMMON_FILTERS = {
  // Search filter
  search: {
    type: 'search',
    name: 'search',
    label: 'Search',
    placeholder: 'Search...',
    colSpan: { xs: 24, md: 8, lg: 6 }
  },

  // Date range filters
  dateRange: {
    type: 'dateRange',
    name: 'dateRange',
    label: 'Date Range',
    colSpan: { xs: 24, md: 8, lg: 6 }
  },

  // Individual date filters
  fromDate: {
    type: 'date',
    name: 'fromDate',
    label: 'From Date',
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  toDate: {
    type: 'date',
    name: 'toDate',
    label: 'To Date',
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  // Month and year filters
  month: {
    type: 'month',
    name: 'month',
    label: 'Month',
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  year: {
    type: 'year',
    name: 'year',
    label: 'Year',
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  // Cost range filters
  minCost: {
    type: 'currency',
    name: 'minCost',
    label: 'Min Cost (₦)',
    placeholder: 'Minimum cost',
    step: 100,
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  maxCost: {
    type: 'currency',
    name: 'maxCost',
    label: 'Max Cost (₦)',
    placeholder: 'Maximum cost',
    step: 100,
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  // Number range filters
  minNumber: {
    type: 'number',
    name: 'minNumber',
    label: 'Min Number',
    placeholder: 'Minimum number',
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  maxNumber: {
    type: 'number',
    name: 'maxNumber',
    label: 'Max Number',
    placeholder: 'Maximum number',
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  // Select filters
  status: {
    type: 'select',
    name: 'status',
    label: 'Status',
    placeholder: 'Select status',
    options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'Pending', value: 'pending' },
      { label: 'Completed', value: 'completed' }
    ],
    colSpan: { xs: 24, sm: 12, md: 6 }
  },

  // Generic select filter
  select: (name, label, options, placeholder) => ({
    type: 'select',
    name,
    label,
    placeholder: placeholder || `Select ${label}`,
    options,
    colSpan: { xs: 24, sm: 12, md: 6 }
  })
};

// Claims-specific filter configurations
export const CLAIMS_FILTERS = {
  // Basic claims filters
  basic: [
    COMMON_FILTERS.search,
    COMMON_FILTERS.fromDate,
    COMMON_FILTERS.toDate,
    COMMON_FILTERS.month,
    COMMON_FILTERS.year
  ],

  // Extended claims filters
  extended: [
    COMMON_FILTERS.search,
    COMMON_FILTERS.fromDate,
    COMMON_FILTERS.toDate,
    COMMON_FILTERS.month,
    COMMON_FILTERS.year,
    COMMON_FILTERS.minCost,
    COMMON_FILTERS.maxCost
  ],

  // Payment-specific filters
  payment: [
    COMMON_FILTERS.search,
    COMMON_FILTERS.fromDate,
    COMMON_FILTERS.toDate,
    // COMMON_FILTERS.month,
    // COMMON_FILTERS.year,
    // COMMON_FILTERS.minCost,
    // COMMON_FILTERS.maxCost,
    // {
    //   type: 'select',
    //   name: 'facilityId',
    //   label: 'Facility',
    //   placeholder: 'Select facility',
    //   options: [], // Will be populated dynamically
    //   colSpan: { xs: 24, sm: 12, md: 6 }
    // },
    // {
    //   type: 'search',
    //   name: 'insuranceNo',
    //   label: 'Insurance Number',
    //   placeholder: 'Enter insurance number',
    //   colSpan: { xs: 24, sm: 12, md: 6 }
    // }
  ],

  // Facility-specific filters
  facility: [
    COMMON_FILTERS.search,
    COMMON_FILTERS.fromDate,
    COMMON_FILTERS.toDate,
    {
      type: 'search',
      name: 'insuranceNo',
      label: 'Insurance Number',
      placeholder: 'Enter insurance number',
      colSpan: { xs: 24, sm: 12, md: 6 }
    },
    COMMON_FILTERS.minCost,
    COMMON_FILTERS.maxCost
  ]
};

// User management filters
export const USER_FILTERS = {
  basic: [
    COMMON_FILTERS.search,
    COMMON_FILTERS.status,
    {
      type: 'select',
      name: 'role',
      label: 'Role',
      placeholder: 'Select role',
      options: [
        { label: 'Admin', value: 'admin' },
        { label: 'User', value: 'user' },
        { label: 'Manager', value: 'manager' }
      ],
      colSpan: { xs: 24, sm: 12, md: 6 }
    }
  ]
};

// Facility management filters
export const FACILITY_FILTERS = {
  basic: [
    COMMON_FILTERS.search,
    {
      type: 'select',
      name: 'state',
      label: 'State',
      placeholder: 'Select state',
      options: [], // Will be populated dynamically
      colSpan: { xs: 24, sm: 12, md: 6 }
    },
    {
      type: 'select',
      name: 'city',
      label: 'City',
      placeholder: 'Select city',
      options: [], // Will be populated dynamically
      colSpan: { xs: 24, sm: 12, md: 6 }
    },
    COMMON_FILTERS.status
  ]
};

// Report filters
export const REPORT_FILTERS = {
  basic: [
    COMMON_FILTERS.dateRange,
    COMMON_FILTERS.month,
    COMMON_FILTERS.year,
    {
      type: 'select',
      name: 'reportType',
      label: 'Report Type',
      placeholder: 'Select report type',
      options: [
        { label: 'Summary', value: 'summary' },
        { label: 'Detailed', value: 'detailed' },
        { label: 'Analytics', value: 'analytics' }
      ],
      colSpan: { xs: 24, sm: 12, md: 6 }
    }
  ]
};

// Helper function to create custom filter configurations
export const createFilterConfig = (filters) => {
  return filters.map(filter => {
    if (typeof filter === 'string' && COMMON_FILTERS[filter]) {
      return COMMON_FILTERS[filter];
    }
    return filter;
  });
};

// Helper function to merge filter configurations
export const mergeFilterConfigs = (...configs) => {
  return configs.flat().filter((filter, index, self) => 
    index === self.findIndex(f => f.name === filter.name)
  );
};

export default {
  COMMON_FILTERS,
  CLAIMS_FILTERS,
  USER_FILTERS,
  FACILITY_FILTERS,
  REPORT_FILTERS,
  createFilterConfig,
  mergeFilterConfigs
};

